import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

// Listens for the browser's `beforeinstallprompt` event (Chrome/Edge/Android)
// and surfaces a tasteful in-app banner that lets the user install MyFamShield
// as a Progressive Web App. iOS Safari does not fire this event — instead it
// requires the user to use Share → Add to Home Screen, so we show a one-line
// instruction the first time iOS users land on the app.
//
// User dismissal is remembered in localStorage so we never nag.

const DISMISS_KEY = 'myfamshield.pwa.installDismissedAt';
const NAG_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function recentlyDismissed() {
	try {
		const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
		return ts && Date.now() - ts < NAG_COOLDOWN_MS;
	} catch {
		return false;
	}
}

function isStandalone() {
	if (typeof window === 'undefined') return false;
	return (
		window.matchMedia?.('(display-mode: standalone)').matches ||
		window.navigator.standalone === true
	);
}

function isIos() {
	if (typeof window === 'undefined') return false;
	const ua = window.navigator.userAgent || '';
	return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}

export default function InstallPrompt() {
	const [deferred, setDeferred] = useState(null);
	const [show, setShow] = useState(false);
	const [iosHint, setIosHint] = useState(false);
	const [installing, setInstalling] = useState(false);

	useEffect(() => {
		if (isStandalone() || recentlyDismissed()) return;

		const onBeforeInstall = (e) => {
			e.preventDefault();
			setDeferred(e);
			setShow(true);
		};
		window.addEventListener('beforeinstallprompt', onBeforeInstall);

		const onInstalled = () => {
			setShow(false);
			setDeferred(null);
		};
		window.addEventListener('appinstalled', onInstalled);

		// iOS fallback: show a small hint the first time, only after a small delay
		// so it doesn't compete with the auth flow.
		if (isIos()) {
			const t = setTimeout(() => setIosHint(true), 6000);
			return () => {
				clearTimeout(t);
				window.removeEventListener('beforeinstallprompt', onBeforeInstall);
				window.removeEventListener('appinstalled', onInstalled);
			};
		}
		return () => {
			window.removeEventListener('beforeinstallprompt', onBeforeInstall);
			window.removeEventListener('appinstalled', onInstalled);
		};
	}, []);

	const dismiss = () => {
		try {
			localStorage.setItem(DISMISS_KEY, String(Date.now()));
		} catch {
			/* ignore */
		}
		setShow(false);
		setIosHint(false);
	};

	const install = async () => {
		if (!deferred) return;
		setInstalling(true);
		try {
			await deferred.prompt();
			const choice = await deferred.userChoice;
			if (choice.outcome === 'accepted') {
				setShow(false);
			} else {
				dismiss();
			}
		} catch {
			/* ignore */
		} finally {
			setInstalling(false);
			setDeferred(null);
		}
	};

	if (!show && !iosHint) return null;

	return (
		<div
			className="fixed bottom-4 right-4 z-40 max-w-sm w-[calc(100%-2rem)] sm:w-[22rem] card-soft p-4 flex items-start gap-3 animate-fade-in-up"
			data-testid="pwa-install-prompt"
		>
			<div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-700 to-emerald-500 text-white grid place-items-center shrink-0">
				<Download className="w-5 h-5" />
			</div>
			<div className="flex-1 min-w-0">
				<div className="font-heading font-semibold text-slate-900 text-sm">
					Install MyFamShield
				</div>
				{show && !iosHint && (
					<p className="text-xs text-slate-600 mt-0.5">
						Add it to your home screen for one-tap access and a smoother family experience.
					</p>
				)}
				{iosHint && (
					<p className="text-xs text-slate-600 mt-0.5">
						On iPhone: tap <span className="font-semibold">Share</span> → <span className="font-semibold">Add to Home Screen</span>.
					</p>
				)}
				<div className="mt-3 flex items-center gap-2">
					{show && !iosHint && (
						<button
							type="button"
							onClick={install}
							disabled={installing}
							data-testid="pwa-install-button"
							className="px-3.5 h-9 rounded-full text-xs font-semibold text-white bg-[#1E3A8A] hover:bg-[#1E40AF] disabled:opacity-60"
						>
							{installing ? 'Installing…' : 'Install app'}
						</button>
					)}
					<button
						type="button"
						onClick={dismiss}
						data-testid="pwa-dismiss-button"
						className="px-3.5 h-9 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-100"
					>
						Not now
					</button>
				</div>
			</div>
			<button
				type="button"
				onClick={dismiss}
				className="text-slate-400 hover:text-slate-600 p-1 rounded-full"
				aria-label="Dismiss install prompt"
			>
				<X className="w-4 h-4" />
			</button>
		</div>
	);
}
